import {v4 as uuidv4} from 'uuid';
import {ScrollDepthMeasurement} from './ScrollDepthMeasurement.js';

import {default as WebKeepAliveSchema} from "./schema/WebKeepAlive.json"
import {default as WebPageviewSchema} from "./schema/WebPageview.json"
import {default as WebScrollSchema} from "./schema/WebScroll.json"

const TRACK_UDP_ACTION = 'srf.track.udp.action';
const TRACK_UDP_SET_USER_ID = 'srf.track.udp.set-user-id';
const TRACK_UDP_UNSET_USER_ID = 'srf.track.udp.unset-user-id';

const createEventHandler = ({
                                eventName,
                                eventHandler,
                                element = window,
                                once = false,
                                passive,
                            }) => {
    const options = {once};
    if (typeof passive !== 'undefined') {
        options.passive = passive;
    }

    const wrappedHandler = (e) => eventHandler.call(element, e);
    element.addEventListener(eventName, wrappedHandler, options);
    return wrappedHandler;
};

const onEvent = (options) => {
    // support multiple events
    if (Array.isArray(options.eventName)) {
        return options.eventName.map((eventNamePart) => {
            const newObject = Object.assign({}, options, {
                eventName: eventNamePart,
            });
            return createEventHandler(newObject);
        });
    } else {
        return createEventHandler(options);
    }
};


export class UDPTracking {
    constructor(host, config = {content_id: null}, tools) {

        let vw = Math.max(document?.documentElement?.clientWidth || 0, window?.innerWidth || 0)
        let vh = Math.max(document?.documentElement?.clientHeight || 0, window?.innerHeight || 0)

        var screen = screen || {}
        const orientation = screen?.orientation?.type?.split('-')[0] ?? 'unknown'

        this.trackingHost = host;
        this.tools = tools;
        this.uuidName = 'srf:analytics:uuid';
        this.userId = undefined;
        this.page_view = Object.assign(config, {
            timestamp: Date.now(),
            type: "page_view",
            platform_id: "srftippspielweb",
            cookie_id: this.getOrCreateUUID(),
            url: window.location.href,
            referrer: document.referrer,
            user_settings: window.matchMedia('(prefers-color-scheme: dark)').matches
                ? 'dark_mode(1)'
                : 'dark_mode(0)',
            user_agent: navigator.userAgent,
            user_agent_data: navigator.userAgentData
                ? JSON.stringify(navigator.userAgentData)
                : null,
            screen_resolution:
                window.screen.availWidth + 'x' + window.screen.availHeight,
            language: navigator.language,
            viewport_size: vw + 'x' + vh,
            screen_orientation: orientation,
            screen_density: window?.devicePixelRatio,
            touch_support: this.hasTouchSupport(),
            vector_id: 'web'
        });

        // how long this website has been open in seconds
        this.timeoutsForKeepAlive = [5, 15, 30, 45, 60, 90, 120, 180, 300, 600, 1200];

        this.initObserver();

    }

    hasTouchSupport() {
        return 'ontouchstart' in window ||
        (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) ||
        (navigator.msMaxTouchPoints && navigator.msMaxTouchPoints > 0)
            ? 'true'
            : 'false';
    }

    getOrCreateUUID() {
        // user ID is stored in local storage and cookie
        // get uuid from either of these
        let uuid_ls = this.tools.getData(this.uuidName);
        uuid_ls = this.isUUID(uuid_ls) ? uuid_ls : null;
        const uuid_cookie = this.getUUIDFromCookie();
        let uuid = uuid_cookie === null ? uuid_ls : uuid_cookie;
        if (uuid === null) {
            // if both are empty, create new uuid
            uuid = this.createUUID();
        }

        // populate uuid
        this.tools.setData(this.uuidName, uuid);
        this.extendUUIDCookieLifetime(uuid);
        return uuid;
    }

    isUUID(inputString) {
        return inputString === null ? false : inputString.match(/^[a-f0-9-]{36}$/);
    }

    getUUIDFromCookie() {
        const matches = document.cookie.match(
            new RegExp(`${this.uuidName}=([a-f0-9\\-]{36})`),
        );
        if (matches && matches.length > 1) {
            return matches[1];
        } else {
            return null;
        }
    }

    createUUID() {
        return uuidv4();
    }

    extendUUIDCookieLifetime(uuid) {
        let d = new Date();
        // cookie is valid 2 years
        d.setTime(d.getTime() + 2 * 365 * 24 * 60 * 60 * 1000);
        document.cookie = `${
            this.uuidName
        }=${uuid};expires=${d.toUTCString()};samesite=strict;path=/`;
    }

    sendPageView() {
        this.page_view.timestamp = Date.now();
        this.validateSchema(this.page_view, WebPageviewSchema);
        this.sendData(JSON.stringify(this.page_view));
    }

    sendData(data) {
        fetch(`${this.trackingHost}/tracking/pageview`, {
            method: 'POST',
            body: data,
            headers: {
                'Content-Type': 'application/json',
            },
        }).catch((error) => {
            // eslint-disable-next-line no-console
            console.warn('Error:', error);
        });
    }

    /**
     * Custom data wrangling for scroll depth tracking according to
     * https://srgssr-ch.atlassian.net/wiki/x/qgp3Lw
     *
     * @param scrollData
     */
    trackScrollEvent(scrollData) {
        const pv = this.page_view;
        const data = Object.assign(
            {
                schema_version: pv.schema_version,
                type: 'ui_event',
                product: pv.product,
                backend_system: pv.backend_system,
                business_unit: pv.business_unit,
                timestamp: Date.now(),
                cookie_id: pv.cookie_id,
                content_id: pv.content_id,
                url: pv.url,
                platform_id: pv.platform_id,
                content_title_pretty: pv.content_title_pretty,
                content_publication_datetime: pv.content_publication_datetime,
                content_category_1: pv.content_category_1,
                vector_id: pv.vector_id,
                user_agent_data: pv.user_agent_data,
                touch_support: pv.touch_support
            },
            scrollData,
        );

        if (this.userId) {
            data.user_id = this.userId;
        }
        this.validateSchema(data, WebScrollSchema);
        this.sendUiEvent(data);
    }

    initObserver() {
        this.timeoutsForKeepAlive.forEach((timeout) => {
            let actionEventData = {
                action_source: this.page_view.content_page_type,
                action_name: 'keep_alive',
                action_type: 'second_value',
                action_value: timeout.toString(),
                type: 'inactive_event',
            };
            setTimeout(() => this.trackEvent(actionEventData, WebKeepAliveSchema), timeout * 1000);
        });

        new ScrollDepthMeasurement(this.page_view.content_page_type, (data) =>
            this.trackScrollEvent(data),
        );

        document.addEventListener('click', (event) => {
            if (event?.target?.getAttribute('data-udp-action-track') !== null) {
                // e.g. <button data-udp-action-track="action_source=radio&action_type=click&action_element=button&action_name=Musik&action_location=last_played"></button>
                const element = event.target;
                // e.g. "action_source=radio&action_type=click&action_element=button&action_name=Musik&action_location=last_played"
                const rawTrackingParams = element.getAttribute('data-udp-action-track');
                const trackingParams = new URLSearchParams(rawTrackingParams);
                // {action_element: "button", action_location: "last_played, "action_name: "Musik", action_source: "radio", action_type: "click"}
                const trackingObject = Object.fromEntries(trackingParams);

                this.trackEvent(trackingObject);
            }
        });

        onEvent({
            eventName: TRACK_UDP_ACTION,
            eventHandler: ({detail}) => this.trackEvent(Object.assign({}, detail)),
        });

        onEvent({
            eventName: TRACK_UDP_SET_USER_ID,
            eventHandler: ({detail: userId}) => this.setUserId(userId),
        });

        onEvent({
            eventName: TRACK_UDP_UNSET_USER_ID,
            eventHandler: () => this.setUserId(undefined),
        });
    }

    trackEvent(eventData = {}, schema) {
        const pv = this.page_view;
        const payload = Object.assign(
            {
                schema_version: pv.schema_version,
                type: 'action_event',
                product: pv.product,
                backend_system: pv.backend_system,
                business_unit: pv.business_unit,
                timestamp: Date.now(),
                cookie_id: pv.cookie_id,
                content_id: pv.content_id,
                url: pv.url,
                content_title_pretty: pv.content_title_pretty,
                content_publication_datetime: pv.content_publication_datetime,
                content_category_1: pv.content_category_1,
                platform_id: pv.platform_id,
                vector_id: pv.vector_id,
                user_agent_data: pv.user_agent_data,
                touch_support: pv.touch_support
            },
            eventData,
        );

        if (this.userId) {
            payload.user_id = this.userId;
        }
        this.validateSchema(payload, schema);
        this.sendUiEvent(payload);
    }

    sendUiEvent(payload = {}) {
        fetch(`${this.trackingHost}/tracking/uievent`, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json',
            },
        }).catch((error) => {
            // eslint-disable-next-line no-console
            console.warn('Error:', error);
        });
    }


    /**
     * Sets the user ID for the current page view. If `undefined` is passed, it is unset.
     *
     * @param {string|undefined} userId The user ID to use for the page view events.
     */
    setUserId(userId) {
        this.userId = userId;
    }

    validateSchema(obj, schema) {
        const errors = [];

        const isFloat = (value) => typeof value === 'number' && !Number.isNaN(value) && !Number.isInteger(value)

        for (const f of schema.fields) {
            const v = obj[f.name];

            if (v == null) {
                if (v === undefined && !f.nullable)
                    errors.push(`${f.name} is required`);
                else if (v === null && !f.nullable)
                    errors.push(`${f.name} cannot be null`);
                continue;
            }

            const typeOK =
                f.type === "string" ? typeof v === "string" :
                    f.type === "long" ? Number.isInteger(v) :
                        f.type === "double" ? isFloat(v) :
                            false;

            if (!typeOK)
                errors.push(`${f.name}: invalid type`);

            if (f.literals && !f.literals.includes(String(v)))
                errors.push(`${f.name}: invalid value "${v}"`);
        }

        const known = new Set(schema.fields.map(f => f.name));
        for (const key of Object.keys(obj))
            if (!known.has(key))
                errors.push(`${key}: unknown field`);

        const isType = (field) => field.name === "type";

        if (errors.length) {
            console.log(schema.fields.find(isType).literals[0]);
            console.log(errors);
        }

        return {valid: !errors.length, errors};
    }
}